import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import authEndpoints from './auth'

import OurClientsEndpoints from './ourClients'

import OfficesEndpoints from './offices'

import RentalsEndpoints from './rentals'

import QuestionsAndAnswersEndpoints from './questionsAndAnswers'

import membershipEndpoints from './membership'

import usersEndpoints from './users'

import OfficeOptionsEndpoints from './office-options'

import feedbackEndpoints from './feedback'

import specificationsEndpoints from './specifications'

import membersdhipPlanEndpoints from './membershipPlan'

import nonMembershipEndpoints from './nonMembership'

import paymentsEndpoints from './payments'

import variableExpensesEndpoints from './variableExpenses'

import employeesEndpoints from './employees'

import fixedExpensesEndpoints from './fixedExpenses'

import recurringExpensesEndpoints from './recurringExpenses'

import partnersEndpoints from './partners'

import otherRevenueEndpoints from './otherRevenue'

import menuTransactionsEndpoints from './menuTransactions'

import runningDashboardEndpoints from './runningDashboard'

import transfersEndpoints from './transfers'



// Use environment variables or sensible defaults

const DcoreUrl = process.env.REACT_APP_D_CORE_URL

// Use the dedicated menu API URL from env. Falls back to production URL if not set.
const envMenuApiUrl = (process.env.REACT_APP_MENU_API_URL || '').replace(/\/$/, '');
const MenuApiUrl = envMenuApiUrl || 'https://backoffice.braxohub.com/api';

// console.log('API Config:', { DcoreUrl, MenuApiUrl, isDevelopment });



const baseQuery = fetchBaseQuery({

  baseUrl: DcoreUrl,

  prepareHeaders: (headers, { endpoint }) => {

    const token = sessionStorage.getItem('token')

    if (token) {

      headers.set('Authorization', `Bearer ${token}`)

    }

    // For file upload endpoints, don't set Content-Type (let browser set it)

    if (endpoint !== 'uploadMenuItemImage' &&

      endpoint !== 'addOfficeImage' &&

      endpoint !== 'uploadClientImage' &&

      endpoint !== 'useUploadRentalImage') {

      headers.set('Content-Type', 'application/json');

    }

    headers.set('accept', 'application/json')

    headers.set('lang', 'ENGLISH')

    return headers

  },

})



// Separate base query for menu API (local backend)

const menuBaseQuery = fetchBaseQuery({

  baseUrl: MenuApiUrl,

  prepareHeaders: (headers, { endpoint }) => {

    const token = sessionStorage.getItem('refreshToken')

    if (token) {

      headers.set('Authorization', `Bearer ${token}`)

    }

    // For file upload endpoints, don't set Content-Type

    if (endpoint !== 'uploadMenuItemImage' &&

      endpoint !== 'uploadEventImage' &&

      endpoint !== 'uploadEventGallery') {

      headers.set('Content-Type', 'application/json');

    }

    headers.set('accept', 'application/json')

    return headers

  },

})



const baseQueryIntercep: typeof baseQuery = async (args, api, extraOptions) => {

  let queryResult = await baseQuery(args, api, extraOptions)

  return queryResult

}



const menuBaseQueryIntercep: typeof menuBaseQuery = async (args, api, extraOptions) => {

  let queryResult = await menuBaseQuery(args, api, extraOptions)

  return queryResult

}



const appApi = createApi({

  reducerPath: 'data',

  baseQuery: (args, api, extraOptions) => {

    // Use menu base query for menu endpoints, regular base query for others

    const isMenuEndpoint = [

      'fetchMenuItems',

      'createMenuItem',

      'updateMenuItem',

      'deleteMenuItem',

      'uploadMenuItemImage',

      'deleteMenuItemImage',

      'fetchMenuItemTransactions',

      'fetchAllMenuTransactions',

      'createTransaction',

      'fetchReportsSummary',

      'fetchTopCategories',

      'fetchDeletedMenuItems',

      'restoreMenuItem',

      'deleteMenuItemPermanent',

      'emptyMenuTrash',

      // categories under menu API

      'fetchCategories',

      'createCategory',

      'updateCategory',

      'deleteCategory',

      // delete-all menu endpoints

      'deleteAllMenuItems',

      'deleteAllTransactions',

      // events

      'fetchEvents',

      'createEvent',

      'updateEvent',

      'deleteEvent',

      'uploadEventImage',

      'uploadEventGallery'

    ].includes(api.endpoint);



    return isMenuEndpoint

      ? menuBaseQueryIntercep(args, api, extraOptions)

      : baseQueryIntercep(args, api, extraOptions);

  },

  tagTypes: [

    'offices',

    'our-clients',

    'rentals',

    'questions-and-answers',

    'members',

    'users',

    'office-options',

    'specifications',

    'membershipPlans',

    'menu',

    'categories',

    'events',

    'variable-expenses',

    'employees',

    'fixed-expenses',

    'recurring-expenses',

    'recurring-expense-occurrences',

    'partners',

    'other-revenue',

    'menu-tx-reviews',

    'running-dashboard',

    'transfers',

  ],

  endpoints: (builder) => ({

    ...authEndpoints(builder),

    ...OfficesEndpoints(builder),

    ...OurClientsEndpoints(builder),

    ...RentalsEndpoints(builder),

    ...QuestionsAndAnswersEndpoints(builder),

    ...membershipEndpoints(builder),

    ...usersEndpoints(builder),

    ...OfficeOptionsEndpoints(builder),

    ...feedbackEndpoints(builder),

    ...specificationsEndpoints(builder),

    ...membersdhipPlanEndpoints(builder),

    ...nonMembershipEndpoints(builder),

    ...paymentsEndpoints(builder),

    ...variableExpensesEndpoints(builder),

    ...employeesEndpoints(builder),

    ...fixedExpensesEndpoints(builder),

    ...recurringExpensesEndpoints(builder),

    ...partnersEndpoints(builder),

    ...otherRevenueEndpoints(builder),

    ...menuTransactionsEndpoints(builder),

    ...runningDashboardEndpoints(builder),

    ...transfersEndpoints(builder),



    // Categories - local backend

    fetchCategories: builder.query<any, any>({

      query: () => '/menu/categories',

      transformResponse: (response: any) => response?.data ?? [],

      providesTags: ['categories']

    }),



    createCategory: builder.mutation<any, any>({

      query: (body) => ({ url: '/menu/categories', method: 'post', body }),

      transformResponse: (response: any) => response?.data ?? response,

      invalidatesTags: ['categories']

    }),



    updateCategory: builder.mutation<any, any>({

      query: ({ id, body }) => ({ url: `/menu/categories/${id}`, method: 'put', body }),

      transformResponse: (response: any) => response?.data ?? response,

      invalidatesTags: ['categories', 'menu']

    }),



    deleteCategory: builder.mutation<any, any>({

      query: (id) => ({ url: `/menu/categories/${id}`, method: 'delete' }),

      invalidatesTags: ['categories', 'menu']

    }),



    // Menu endpoints - Will use local backend

    fetchMenuItems: builder.query<any, any>({

      query: () => '/menu/items',

      // Backend may return an array (simple server) or an object { success, data }

      transformResponse: (response: any) => Array.isArray(response) ? response : response?.data ?? [],

      providesTags: ['menu']

    }),



    createMenuItem: builder.mutation<any, any>({

      query: (body) => ({

        url: '/menu/items',

        method: 'post',

        body,

      }),

      // Unwrap { success, data } to the created item

      transformResponse: (response: any) => response?.data ?? response,

      invalidatesTags: ['menu']

    }),



    deleteMenuItem: builder.mutation<any, any>({

      query: (id) => ({

        url: `/menu/items/${id}`,

        method: 'delete',

      }),

      invalidatesTags: ['menu'],

    }),



    updateMenuItem: builder.mutation<any, any>({

      query: ({ id, body }) => ({

        url: `/menu/items/${id}`,

        method: 'put',

        body,

      }),

      transformResponse: (response: any) => response?.data ?? response,

      invalidatesTags: ['menu'],

    }),



    uploadMenuItemImage: builder.mutation<any, any>({

      query: (body) => ({

        url: '/menu/items/images',

        method: 'post',

        body,

      }),

      invalidatesTags: ['menu'],

    }),



    deleteMenuItemImage: builder.mutation<any, any>({

      query: (id) => ({

        url: `/menu/items/images/${id}`,

        method: 'delete',

      }),

      invalidatesTags: ['menu'],

    }),



    fetchMenuItemTransactions: builder.query<any, any>({

      query: (menuItemId) => `/menu/items/${menuItemId}/transactions`,

      transformResponse: (response: any) => response?.data ?? [],

      providesTags: ['menu']

    }),



    fetchAllMenuTransactions: builder.query<any, { page?: number; size?: number; startDate?: string; endDate?: string; type?: string; sortBy?: string; sortDir?: 'asc' | 'desc' }>({

      query: ({ page = 0, size = 20, startDate, endDate, type, sortBy, sortDir } = {}) => {

        const params = new URLSearchParams()

        params.set('page', String(page))

        params.set('size', String(size))

        if (startDate) params.set('startDate', startDate)

        if (endDate) params.set('endDate', endDate)

        if (type) params.set('type', type)

        if (sortBy) params.set('sortBy', sortBy)

        if (sortDir) params.set('sortDir', sortDir)

        return { url: `/menu/transactions?${params.toString()}`, method: 'get' }

      },

      providesTags: ['menu']

    }),



    createTransaction: builder.mutation<any, any>({

      query: ({ menuItemId, body }) => ({

        url: `/menu/items/${menuItemId}/transactions`,

        method: 'post',

        body,

      }),

      transformResponse: (response: any) => response?.data ?? response,

      invalidatesTags: ['menu'],

    }),



    fetchReportsSummary: builder.query<any, { from?: string, to?: string }>({

      query: (params) => ({

        url: '/menu/reports/summary',

        params

      }),

      transformResponse: (response: any) => response?.data,

      providesTags: ['menu']

    }),



    fetchTopCategories: builder.query<any, { from?: string, to?: string, limit?: number }>({

      query: (params) => ({

        url: '/menu/reports/top-categories',

        params

      }),

      transformResponse: (response: any) => response?.data,

    }),



    fetchDeletedMenuItems: builder.query<any, void>({

      query: () => '/menu/items/deleted',

      transformResponse: (response: any) => response?.data ?? [],

      providesTags: ['menu']

    }),



    restoreMenuItem: builder.mutation<any, string>({

      query: (id) => ({

        url: `/menu/items/${id}/restore`,

        method: 'put',

      }),

      invalidatesTags: ['menu']

    }),



    deleteMenuItemPermanent: builder.mutation<any, string>({
      query: (id) => ({
        url: `/menu/items/${id}/permanent`,
        method: 'delete',
      }),
      invalidatesTags: ['menu']
    }),

    emptyMenuTrash: builder.mutation<{ success: boolean; message: string; deletedCount: number }, void>({
      query: () => ({
        url: '/menu/items/trash/empty',
        method: 'delete',
      }),
      invalidatesTags: ['menu']
    }),

    // Events endpoints

    fetchEvents: builder.query<{ success: boolean; data: any[]; count: number }, void>({

      query: () => '/menu/events',

      providesTags: ['events']

    }),



    createEvent: builder.mutation<any, any>({

      query: (body) => ({ url: '/menu/events', method: 'post', body }),

      invalidatesTags: ['events']

    }),



    updateEvent: builder.mutation<any, { id: string, body: any }>({

      query: ({ id, body }) => ({ url: `/menu/events/${id}`, method: 'put', body }),

      invalidatesTags: ['events']

    }),



    deleteEvent: builder.mutation<any, string>({

      query: (id) => ({ url: `/menu/events/${id}`, method: 'delete' }),

      invalidatesTags: ['events']

    }),



    uploadEventImage: builder.mutation<any, any>({

      query: (body) => ({

        url: '/menu/events/images',

        method: 'post',

        body,

      }),

      invalidatesTags: ['events'],

    }),



    uploadEventGallery: builder.mutation<any, { id: string, body: any }>({

      query: ({ id, body }) => ({

        url: `/menu/events/${id}/gallery`,

        method: 'post',

        body,

      }),

      invalidatesTags: ['events'],

    }),



    deleteEventImage: builder.mutation<any, string>({

      query: (id) => ({

        url: `/menu/events/${id}/images`,

        method: 'delete',

      }),

      invalidatesTags: ['events'],

    }),



    deleteEventGalleryImage: builder.mutation<any, { id: string, imageUrl: string }>({

      query: ({ id, imageUrl }) => ({

        url: `/menu/events/${id}/gallery`,

        method: 'delete',

        body: { imageUrl },

      }),

      invalidatesTags: ['events'],

    }),

  }),

})



export default appApi



export const {

  useLoginMutation,



  // Offices 

  useFetchOfficesQuery,

  useCreateOfficeMutation,

  useDeleteOfficeMutation,

  useAddOfficeImageMutation,

  useDeleteOfficeImageMutation,

  useDeleteThisImageMutation,

  useUpdateOfficeMutation,

  useDeleteOfficeSpecificationsMutation,

  useAddOfficeSpecificationMutation,



  // Our Clients

  useFetchOurClientsQuery,

  useCreateOurClientMutation,

  useDeleteOurClientMutation,

  useUpdateOurClientMutation,

  useUploadClientImageMutation,

  useDeleteClientImageMutation,



  // Rentals

  useFetchRentalsQuery,

  useCreateRentalMutation,

  useDeleteRentalMutation,

  useUpdateRentalsMutation,

  useAddSpecificationMutation,

  useDeleteSpecificationMutation,

  useUseUploadRentalImageMutation,

  useDeleteRentalImageMutation,



  // Questions and Answers

  useFetchQuestionsAndAnswersQuery,

  useCreateQuestionAndAnswerMutation,

  useDeleteQuestionAndAnswerMutation,

  useUpdateQuestionAndAnswerMutation,



  // Membership

  useFetchMembersQuery,

  useCreateMemberMutation,



  // Users

  useFetchUsersQuery,

  useFetchMembersSummaryQuery,

  useFetchMemberIncomeReportQuery,

  useCreateUserMutation,

  useAddMembershipMutation,

  useLazySearchAUserQuery,

  useUpdateUserMutation,

  useDeleteUserMutation,

  useChangeUserTypeMutation,

  useAssignMembershipToUserMutation,

  useAssignRentalToUserMutation,

  useDeleteAssignedUserMembershipMutation,

  useDeleteAssignedRentalMutation,

  useHasReturnedRentalMutation,

  useGetAssignedRentalDetailsQuery,

  useFetchUserPaymentsQuery,

  useCompleteUserPaymentMutation,

  useCancelUserRentalMutation,

  useEditMembershipImageMutation,



  // Office Options

  useCreateOfficeOptionMutation,

  useFetchOfficeOptionsQuery,

  useDeleteOfficeOptionMutation,

  useUpdateOfficeOptionMutation,



  useFetchFeedbackQuery,

  useFetchEmailSubscriptionsQuery,



  // specifications

  useFetchSpecificationsQuery,

  useCreateSpecificationMutation,

  useDeleteSpecificationSettingsMutation,

  useUpdateSpecificationMutation,



  // membership plans

  useFetchMembershipPlansQuery,

  useCreateMembershipPlanMutation,

  useDeleteMembershipPlanMutation,

  useUpdateMembershipPlanMutation,



  // non membership

  useFetchNonMembersQuery,

  useCreateNonMemberMutation,

  useUpdateNonMemberMutation,

  useDeleteNonMemberMutation,

  useAssignOfficeToUserMutation,



  // Categories

  useFetchCategoriesQuery,

  useCreateCategoryMutation,

  useUpdateCategoryMutation,

  useDeleteCategoryMutation,



  // Menu - Updated exports

  useFetchMenuItemsQuery,

  useCreateMenuItemMutation,

  useUpdateMenuItemMutation,

  useDeleteMenuItemMutation,

  useUploadMenuItemImageMutation,

  useDeleteMenuItemImageMutation,

  useFetchMenuItemTransactionsQuery,

  useCreateTransactionMutation,

  useFetchReportsSummaryQuery,

  useFetchTopCategoriesQuery,



  // Events

  useFetchEventsQuery,

  useCreateEventMutation,

  useUpdateEventMutation,

  useDeleteEventMutation,

  useUploadEventImageMutation,

  useUploadEventGalleryMutation,

  useDeleteEventImageMutation,

  useDeleteEventGalleryImageMutation,



  useFetchDeletedMenuItemsQuery,
  useRestoreMenuItemMutation,
  useDeleteMenuItemPermanentMutation,
  useEmptyMenuTrashMutation,



  // Payments

  useFetchPaymentsQuery,

  useFetchLedgerQuery,

  useFetchRevenueReportQuery,

  useFetchRevenueCustomersQuery,

  useReviewPaymentMutation,

  useUpdateReviewedPaymentMutation,



  // Variable Expenses

  useFetchVariableExpensesQuery,

  useCreateVariableExpenseMutation,

  useUpdateVariableExpenseMutation,

  useDeleteVariableExpenseMutation,

  useFetchVariableExpensesReportQuery,

  useReviewVariableExpenseMutation,

  useUpdateReviewedVariableExpenseMutation,



  // Employees

  useFetchEmployeesQuery,

  useCreateEmployeeMutation,

  useUpdateEmployeeMutation,

  useDeleteEmployeeMutation,



  // Fixed Expenses

  useFetchFixedExpensesQuery,

  useCreateFixedExpenseMutation,

  useUpdateFixedExpenseMutation,

  useDeleteFixedExpenseMutation,

  useFetchFixedExpensesReportQuery,

  useReviewFixedExpenseMutation,

  useUpdateReviewedFixedExpenseMutation,

  // Recurring Expenses (one parent per expense, occurrences recorded underneath)

  useFetchRecurringExpensesQuery,

  useFetchRecurringExpenseQuery,

  useFetchDueRecurringExpensesQuery,

  useFetchRecurringExpensesReportQuery,

  useCreateRecurringExpenseMutation,

  useUpdateRecurringExpenseMutation,

  useDeleteRecurringExpenseMutation,

  useFetchRecurringExpenseOccurrencesQuery,

  useAddRecurringExpenseOccurrenceMutation,

  useUpdateRecurringExpenseOccurrenceMutation,

  useDeleteRecurringExpenseOccurrenceMutation,

  useReviewRecurringExpenseOccurrenceMutation,

  useUpdateReviewedRecurringExpenseOccurrenceMutation,



  // Partners

  useFetchPartnersQuery,

  useFetchPartnerByIdQuery,

  useCreatePartnerMutation,

  useUpdatePartnerMutation,

  useDeletePartnerMutation,

  useFetchPartnerMembersQuery,

  useCreatePartnerMemberMutation,

  useUpdatePartnerMemberMutation,

  useDeletePartnerMemberMutation,



  // Other Revenue

  useFetchOtherRevenuesQuery,

  useCreateOtherRevenueMutation,

  useUpdateOtherRevenueMutation,

  useDeleteOtherRevenueMutation,

  useFetchOtherRevenueReportQuery,

  useReviewOtherRevenueMutation,

  useUpdateReviewedOtherRevenueMutation,



  // Menu Transactions (Node-menu + Java-review split)

  useFetchAllMenuTransactionsQuery,

  useLookupMenuTransactionReviewsMutation,

  useReviewMenuTransactionMutation,

  useUpdateReviewedMenuTransactionMutation,



  // Running Dashboard

  useFetchRunningDashboardsQuery,

  useFetchRunningDashboardByIdQuery,

  useCreateRunningDashboardMutation,

  useUpdateRunningDashboardMutation,

  useDeleteRunningDashboardMutation,



  // Transfers

  useFetchTransfersQuery,

  useCreateTransferMutation,

  useDeleteTransferMutation,

  useFetchTransferFeesReportQuery,



} = appApi
